import { Tile, TileLayoutItem, TileSize } from '../types/tile.types';

/** Sortiert Kacheln nach dem gespeicherten Layout. Kacheln, die im Layout noch fehlen (neu
 * freigegeben oder neu angelegt), behalten ihre bisherige Reihenfolge und landen am Ende. */
export function orderTilesByLayout(tiles: Tile[], layout: TileLayoutItem[]): Tile[] {
  if (!layout.length) return tiles;
  const position = new Map(layout.map((item, index) => [item.tileId, index]));
  const known = tiles.filter(tile => position.has(tile._id)).sort((a, b) => position.get(a._id)! - position.get(b._id)!);
  const unknown = tiles.filter(tile => !position.has(tile._id));
  return [...known, ...unknown];
}

/** Baut das zu speichernde Layout aus der aktuellen Reihenfolge, den Größen und den entfernten Kacheln. */
export function toTileLayout(tiles: Tile[], sizes: Record<string, TileSize>, hidden: ReadonlySet<string> = new Set()): TileLayoutItem[] {
  return tiles.map(tile => tile.separator
    ? { tileId: tile._id, size: '1x1', separator: true, title: tile.name }
    : { tileId: tile._id, size: sizes[tile._id] ?? '1x1', ...(hidden.has(tile._id) ? { hidden: true } : {}) });
}

/** Feste ID des automatisch erzeugten Separators über den Kacheln außerhalb des Standardlayouts. */
export const EXTRA_TILES_SEPARATOR_ID = 'sep:nur-fuer-dich';

export const MAX_SEPARATOR_TITLE_LENGTH = 80;

/** Ein Separator aus dem Layout als Eintrag fürs Kachelraster. */
export function separatorTile(item: Pick<TileLayoutItem, 'tileId' | 'title'>): Tile {
  const title = item.title ?? '';
  return { _id: item.tileId, name: title, text: title, subtext: '', url: '', openInNewTab: false, color: '', icon: '', kind: 'link', permissions: [], separator: true };
}

/** Neue Separator-ID, eindeutig genug für die Separatoren eines Layouts. */
export function newSeparatorId(): string {
  const random = globalThis.crypto?.randomUUID?.().replace(/-/g, '') ?? Math.random().toString(36).slice(2);
  return `sep:${random.slice(0, 20)}`;
}

/** Startseite eines Nutzers ohne eigenes Layout (#116): das Standardlayout des Mandanten, ohne
 * Kacheln, die der Nutzer nicht sieht. Alle übrigen Kacheln des Nutzers (individuell freigegebene,
 * persönliche, neu freigeschaltete) folgen am Ende unter einem Separator „nur für dich“.
 * Ohne Standardlayout bleibt es bei der eingebauten Reihenfolge (leeres Layout). */
export function resolveDefaultLayout(defaultItems: TileLayoutItem[], tiles: Tile[], extraTitle: string): TileLayoutItem[] {
  if (!defaultItems.length) return [];
  const visibleIds = new Set(tiles.map(tile => tile._id));
  const kept = defaultItems.filter(item => item.separator || visibleIds.has(item.tileId));
  const placed = new Set(kept.map(item => item.tileId));
  const extras = tiles.filter(tile => !placed.has(tile._id));
  if (!extras.length) return kept;
  return [
    ...kept,
    { tileId: EXTRA_TILES_SEPARATOR_ID, size: '1x1', separator: true, title: extraTitle },
    ...extras.map((tile): TileLayoutItem => ({ tileId: tile._id, size: '1x1' })),
  ];
}

/** Liegt der Zeiger im inneren Bereich der Kachel? `inset` ist der Rand je Seite als Anteil von
 * Breite bzw. Höhe, der nicht zählt (0.25 = nur die mittlere Hälfte). */
export function isInSnapZone(rect: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>, x: number, y: number, inset = 0.25): boolean {
  const insetX = (rect.right - rect.left) * inset;
  const insetY = (rect.bottom - rect.top) * inset;
  return x >= rect.left + insetX && x <= rect.right - insetX && y >= rect.top + insetY && y <= rect.bottom - insetY;
}
